import { useMemo, useState, type ReactElement } from 'react';
import { DEFAULT_LIST_PAGE_SIZE } from '../../constants';
import { paginateRows } from '../../domain/contracts';
import { GroupedDataGrid, type GroupedColumnDef } from '../grid/GroupedDataGrid';
import { GridPaginationBar } from '../grids/GridPaginationBar';
import { EntityActivitySection } from '../activity/EntityActivitySection';
import { FilterTiles } from './FilterTiles';
import { MasterRecordPanel } from './MasterRecordPanel';
import { MasterSearchField } from './searchField';

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

export interface TractRow {
  id: string;
  name: string;
  state: string;
  county: string;
  surveySystem: string;
}

const COLUMNS: GroupedColumnDef[] = [
  { id: 'name', label: 'Name', header: 'Name' },
  { id: 'state', label: 'State', header: 'State' },
  { id: 'county', label: 'County', header: 'County' },
  { id: 'survey', label: 'Survey', header: 'Survey' }
];

function groupValue(row: TractRow, columnId: string): string {
  switch (columnId) {
    case 'name':
      return row.name;
    case 'state':
      return row.state;
    case 'county':
      return row.county;
    case 'survey':
      return row.surveySystem;
    default:
      return '—';
  }
}

export function TractDetail({
  item,
  variant = 'panel',
  onClose,
  onBack,
  authFetch
}: {
  item: TractRow;
  variant?: 'panel' | 'page';
  onClose?: () => void;
  onBack?: () => void;
  authFetch?: AuthFetch;
}): ReactElement {
  return (
    <MasterRecordPanel
      testId="tract-side-panel"
      ariaLabel="Tract details"
      title={item.name}
      status={item.surveySystem}
      variant={variant}
      onClose={onClose}
      onBack={onBack}
    >
      <dl className="detail-list">
        <dt>Name</dt>
        <dd>{item.name}</dd>
        <dt>State</dt>
        <dd>{item.state}</dd>
        <dt>County</dt>
        <dd>{item.county}</dd>
        <dt>Survey</dt>
        <dd>
          <span className="status-pill">{item.surveySystem}</span>
        </dd>
      </dl>
      {authFetch && (
        <EntityActivitySection authFetch={authFetch} path={`/tracts/${item.id}/activity`} testId="tract-activity" />
      )}
    </MasterRecordPanel>
  );
}

export function TractsRegister({
  rows,
  query,
  onQuery,
  onOpen,
  authFetch
}: {
  rows: TractRow[];
  query: string;
  onQuery: (value: string) => void;
  onOpen?: (id: string) => void;
  authFetch?: AuthFetch;
}): ReactElement {
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);

  const surveys = useMemo(() => [...new Set(rows.map((r) => r.surveySystem).filter(Boolean))], [rows]);
  const filtered = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.surveySystem === filter)),
    [filter, rows]
  );
  const pageRows = useMemo(() => paginateRows(filtered, page, pageSize), [filtered, page, pageSize]);
  const selected = rows.find((r) => r.id === selectedId) ?? null;

  return (
    <>
      <FilterTiles
        testId="tract-tiles"
        value={filter}
        onChange={(next) => {
          setFilter(next);
          setPage(1);
          setSelectedId(null);
        }}
        options={[
          { id: 'all', label: 'All', count: rows.length },
          ...surveys.map((survey) => ({
            id: survey,
            label: survey,
            count: rows.filter((r) => r.surveySystem === survey).length
          }))
        ]}
      />
      <div className={`requests-shell${selectedId ? ' has-panel' : ''}`}>
        <section className="card requests-grid-card" data-testid="tracts-grid">
          <div className="row spread requests-grid-toolbar">
            <h2>Tracts</h2>
          </div>
          <p className="hint">Click a row for the side panel; double-click to open the full page.</p>
          <MasterSearchField value={query} onChange={onQuery} />
          <GroupedDataGrid<TractRow>
            columns={COLUMNS}
            rows={pageRows}
            getRowId={(r) => r.id}
            getGroupValue={groupValue}
            colSpan={4}
            emptyMessage="No tracts match this status."
            groupHint="Click a column title to sort. Drag the grip beside a title into the strip to group."
            renderRow={(row) => (
              <tr
                className={selectedId === row.id ? 'selected' : undefined}
                data-testid="tract-row"
                onClick={() => setSelectedId(row.id)}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  onOpen?.(row.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedId(row.id);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <td>{row.name}</td>
                <td className="mono-id">{row.state}</td>
                <td>{row.county}</td>
                <td>
                  <span className="status-pill">{row.surveySystem}</span>
                </td>
              </tr>
            )}
          />
          <GridPaginationBar
            idPrefix="tracts"
            page={page}
            pageSize={pageSize}
            totalCount={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </section>
        {selected && <TractDetail item={selected} authFetch={authFetch} onClose={() => setSelectedId(null)} />}
      </div>
    </>
  );
}
